/**
 * EM QUE SITUAÇÃO CADA CONTRATO ESTÁ, para o reajuste.
 *
 * Espelho de `helpers/situacao-reajuste.util` do backend, refeito aqui só para
 * LISTAR quem está em cada balde — os CONTADORES continuam vindo de lá, que tem
 * guardrail. Se os dois divergirem, o backend é que vale.
 *
 * Mora fora do componente porque a ordem das condições é a regra inteira, e
 * errar a ordem não dá erro em lugar nenhum: o contrato cai no balde errado, a
 * tela fica bonita, e alguém deixa de perguntar ao órgão ou passa a cobrar
 * perda de um contrato que não fatura.
 *
 * ⚠️ SUSPENSO (backend #234) entra em UM balde só, `sem_dado_do_orgao`. Serviço
 * não ativado e data-base que só o órgão tem são coisas independentes: esconder
 * o suspenso faria a pergunta ao órgão nunca ser feita. Nos outros quatro ele
 * não pode entrar — "vencido sem anualidade" num contrato que ainda não fatura
 * inventaria uma perda que não está acontecendo.
 */

export type Situacao =
  | 'pendente'
  | 'vencido_sem_anualidade'
  | 'sem_dado_do_orgao'
  | 'a_conferir'
  | 'em_dia';

export const SITUACOES: Situacao[] = [
  'pendente',
  'vencido_sem_anualidade',
  'sem_dado_do_orgao',
  'a_conferir',
  'em_dia',
];

export interface ContratoClassificavel {
  id: number | string;
  status?: string | null;
  adjustmentVerificationStatus?: string | null;
  nextAdjustmentDate?: string | null;
  adjustmentIndex?: string | null;
  adjustmentBaseKind?: string | null;
  adjustmentRuleSource?: string | null;
  adjustmentBaseDate?: string | null;
  [k: string]: any;
}

export interface ContextoDaClassificacao {
  /** Contratos com anualidade devida, pedida ou deferida — o que está em curso. */
  comAnualidadeViva: Set<number>;
  /** Contratos que já têm qualquer anualidade, em qualquer status. */
  comAlgumaAnualidade: Set<number>;
  /** `YYYY-MM-DD`. Parâmetro para o teste poder fixar o dia. */
  hoje: string;
}

/** O cadastro conta a história: índice, espécie da data-base e a fonte da regra. */
const regraDescrita = (c: ContratoClassificavel): boolean =>
  !!c.adjustmentIndex && !!c.adjustmentBaseKind && !!c.adjustmentRuleSource;

export function situacaoDoContrato(
  c: ContratoClassificavel,
  ctx: ContextoDaClassificacao,
): Situacao | null {
  const id = Number(c.id);
  const conferida = (c.adjustmentVerificationStatus || 'nao_conferido') !== 'nao_conferido';
  const prox = c.nextAdjustmentDate ? String(c.nextAdjustmentDate).slice(0, 10) : null;
  const semDataBase = !c.adjustmentBaseDate;

  if (c.status === 'Suspenso') {
    // Único balde que o aceita. Fora dele, não classifica.
    return regraDescrita(c) && semDataBase ? 'sem_dado_do_orgao' : null;
  }
  if (c.status !== 'Ativo') return null;

  if (ctx.comAnualidadeViva.has(id)) return 'pendente';
  if (conferida && prox && prox <= ctx.hoje && !ctx.comAlgumaAnualidade.has(id)) {
    return 'vencido_sem_anualidade';
  }
  if (regraDescrita(c) && semDataBase) return 'sem_dado_do_orgao';
  if (!conferida) return 'a_conferir';
  return 'em_dia';
}

export function classificarContratos(
  contratos: ContratoClassificavel[],
  ctx: ContextoDaClassificacao,
): Record<Situacao, ContratoClassificavel[]> {
  const baldes = {
    pendente: [], vencido_sem_anualidade: [], sem_dado_do_orgao: [], a_conferir: [], em_dia: [],
  } as Record<Situacao, ContratoClassificavel[]>;
  for (const c of contratos || []) {
    const s = situacaoDoContrato(c, ctx);
    if (s) baldes[s].push(c);
  }
  return baldes;
}
